/**
 * Sanctum Lock + Privacy Veil client state (v0.10.0).
 *
 * Lock: anti-snooping overlay after idle timeout (and on demand). The passcode
 * is verified SERVER-side — clearing localStorage cannot skip verification,
 * because the locked flag is re-derived from the server settings on load.
 * Honest scope: protects a device left open, not a stolen token.
 *
 * Veil: render aliases/initials instead of real names. Preference lives
 * server-side; cached here for instant first paint.
 */

const LAST_ACTIVE = 'fa_last_active'
const LOCKED = 'fa_locked'
const VEIL = 'fa_veil'

let lockEnabled = false
let timeoutMins = 5
const listeners = new Set<() => void>()

export function configureLock(enabled: boolean, mins: number) {
  lockEnabled = enabled
  timeoutMins = mins
  if (!enabled) localStorage.removeItem(LOCKED)
  emit()
}

export function touchActivity() {
  localStorage.setItem(LAST_ACTIVE, String(Date.now()))
}

export function shouldBeLocked(): boolean {
  if (!lockEnabled) return false
  if (localStorage.getItem(LOCKED) === '1') return true
  const last = Number(localStorage.getItem(LAST_ACTIVE) || 0)
  return last > 0 && Date.now() - last > timeoutMins * 60_000
}

export function lockNow() {
  if (!lockEnabled) return
  localStorage.setItem(LOCKED, '1')
  emit()
}

export function markUnlocked() {
  localStorage.removeItem(LOCKED)
  touchActivity()
  emit()
}

export function isLockConfigured(): boolean {
  return lockEnabled
}

// ── veil ──
export function getVeil(): boolean {
  return localStorage.getItem(VEIL) === '1'
}

export function setVeilLocal(on: boolean) {
  localStorage.setItem(VEIL, on ? '1' : '0')
  emit()
}

/** Mask a person's name when the veil is on: alias if set, else initials. */
export function maskName(p: { first_name: string; last_name?: string | null; alias?: string | null }, veiled?: boolean): string {
  const on = veiled ?? getVeil()
  const full = `${p.first_name} ${p.last_name || ''}`.trim()
  if (!on) return full
  if (p.alias) return p.alias
  const fi = (p.first_name || '?')[0]?.toUpperCase() || '?'
  const li = (p.last_name || '')[0]?.toUpperCase() || ''
  return li ? `${fi}. ${li}.` : `${fi}.`
}

/** Mask a plain display string (emails/names in lists). */
export function maskText(s: string, veiled?: boolean): string {
  const on = veiled ?? getVeil()
  if (!on || !s) return s
  return s.split(/\s+/).map(w => (w[0] || '') + '·'.repeat(Math.max(1, Math.min(4, w.length - 1)))).join(' ')
}

/**
 * Living-person PII protection (v0.11.0) — ALWAYS on, independent of the veil.
 * Full name + exact DOB + mother's maiden name is a knowledge-based-authentication
 * goldmine (research-lock #4), so for LIVING people we never render the exact birth
 * date or the maiden name in display surfaces — only the birth year. The owner/editor
 * still sees and edits the real values in forms (canEdit passes through untouched).
 */
export function displayBirthDate(p: { birth_date?: string | null; is_living?: number | boolean }, canEdit?: boolean): string {
  const d = p.birth_date || ''
  if (!d) return ''
  const living = p.is_living === 1 || p.is_living === true
  if (living && !canEdit) return d.slice(0, 4)      // year only for living people
  return d
}

export function displayMaidenName(p: { maiden_name?: string | null; is_living?: number | boolean }, canEdit?: boolean): string {
  const living = p.is_living === 1 || p.is_living === true
  if (living && !canEdit) return ''                  // hide living maiden name (KBA surface)
  return p.maiden_name || ''
}

export function subscribeSecurity(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function emit() {
  listeners.forEach(l => l())
}
